module Form.StudentSelect where

import Common

import API.Account (AccountType(..), AccountInfo)
import API.Pagination (Page, changePage, pageItems)
import API.Search (SearchData, SearchSemester, SearchTeacher, searchDataQuery)
import API.SelStudent (SelStudent, searchStudents, studentName, tagImported)
import API.Types (StudentID)
import Concur.Core (Widget)
import Concur.Core.Patterns (loopState)
import Concur.React (HTML)
import Concur.React.DOM as D
import Concur.React.Props as P
import Control.Alt ((<|>))
import Control.Monad.Except.Trans (ExceptT(..), runExceptT)
import Data.Array (catMaybes, cons)
import Data.Array as Array
import Data.Either (Either(..))
import Data.Map (Map)
import Data.Map as Map
import Data.Maybe (Maybe(..))
import Data.Time.Duration (Milliseconds(..))
import Effect.Aff (delay)
import Error (reportError)
import GraphQL.Join (runGraphQL)
import GraphQL.Type (innerId)
import Internal.ConcurUtil (loopInfinitely)
import Internal.SelectionMap (SelectionMap)
import Internal.SelectionMap as Sel
import Internal.Util (justIf)
import Steps.WorkQueue.Cache (WorkQueueCache)
import Steps.WorkQueue.State (WorkQueueState, isWorkQueueEmpty)
import Steps.WorkQueue.UIState (WorkQueueUiState)
import Steps.WorkQueue.View (WorkQueueResult, workQueueWidget)
import Widget.Pager (pagerWidget)
import Widgets.BetterSelect (Select, selectedItem)
import Widgets.Checkbox (labelCheckbox)
import Widgets.HiddenSelect (hiddenSelect)
import Widgets.Loading (loadingWidget)
import Widgets.Modal (ModalResult(..), errorModalWidget, modalWidget')
import Widgets.Search (SearchState, hasFiltersSet, initSearchState, loadSearchState, searchWidget, stateToQuery)


debounceDelay :: Milliseconds
debounceDelay = Milliseconds 300.0

type SelectStudentState =
    { page :: Maybe (Page SelStudent)
    , students :: SelectionMap StudentID SelStudent
    , search :: SearchState
    , status :: SelectStudentStatus
    , account :: AccountInfo
    , msg :: Maybe String
    }
data SelectStudentStatus = SNormal | SLoading | SError ErrorWrapper

initState :: WorkQueueState -> WorkQueueCache -> SelectStudentState
initState workQueueState workQueueCache =
    { page: Nothing
    , students: workQueueState.students
    , search: loadSearchState $ workQueueCache.searchData { defaults { teacher = Nothing, group = Nothing } }
    , status: SNormal
    , msg: Nothing
    , account: workQueueCache.account
    }

initState' :: Maybe SearchState -> SearchData -> SelectStudentState
initState' search sdata =
    { page: Nothing
    , students: Sel.empty
    , search: fromMaybe (loadSearchState sdata) search
    , status: SNormal
    , msg: Nothing
    , account: { type: TeacherAccount, student: Nothing }
    }

updatePage :: Page SelStudent -> SelectStudentState -> SelectStudentState
updatePage page state = state { page = Just page }

select :: SelStudent -> SelectStudentState -> SelectStudentState
select student state = state { students = Sel.select' student state.students }

deselect :: SelStudent -> SelectStudentState -> SelectStudentState
deselect student state = state { students = Sel.deselect student.id state.students }

isSelected :: SelStudent -> SelectStudentState -> Boolean
isSelected student state = Sel.isSelected student.id state.students

areAllSelected :: SelectStudentState -> Boolean
areAllSelected { page: Just page, students } =
    Sel.areAllSelected (map _.id $ pageItems page) students
areAllSelected _ = false

studentsToMap :: Array SelStudent -> Map StudentID SelStudent
studentsToMap = Map.fromFoldable <<< map (\s -> Tuple s.id s)

setAllSelected :: Boolean -> SelectStudentState -> SelectStudentState
setAllSelected sel state@{ page: Just page } =
    state { students = Sel.selectAll' (pageItems page) state.students sel }
setAllSelected _ state@{ page: Nothing } = state



data SelectStudentAction
    -- | Search query changed
    = ChangeSearch SearchState
    | ChangePage Int
    | QueryResult (Page SelStudent)
    | QueryFailed ErrorWrapper

data SelectStudentStepAction
    = StateChange SelectStudentState
    | WorkQA WorkQueueResult
    | QueueChanged WorkQueueState WorkQueueUiState
    | Done | Cancel | Export



-- | Render the student select widget and WorkQueue with the given state.
studentSelectStepWidget
    :: WorkQueueState
    -> WorkQueueUiState
    -> WorkQueueCache
    -> SelectStudentState
    -> Widget HTML SelectStudentStepAction
studentSelectStepWidget workQueueState workQueueUi workQueueCache state = do
    D.main [P.className "step-container step-2a", P.role "main"] $ catMaybes
        [ Just $ D.header
            [ P.className "page-header" ]
            [ D.h1
                [ P.className "page-heading" ]
                [ D.text $ lookupTranslation "select-students" ]
            , actionBtns
            ]
        , Just $ WorkQA <$> workQueueWidget workQueueState workQueueUi workQueueCache
        , (\msg -> D.div [ P.className "error" ] [ D.text msg ]) <$> state.msg
        , Just $ D.div
            [ P.className "teacher-semester-selections" ] $ catMaybes
            [ Just $ D.p' [ D.text $ lookupTranslation "semester-hidden-select" ]
            , Just (setSemesterSelect workQueueState workQueueUi <$> hiddenSelect workQueueUi.semester)
            , justIf notBoardAcc $
                D.p' [ D.text $ lookupTranslation "teacher-hidden-select" ]
            , justIf notBoardAcc
                (setTeacherSelect workQueueState workQueueUi <$> hiddenSelect workQueueUi.teacher)
            ]
        , Just $ D.div
            [ P.className "step-2a-right" ]
            [ StateChange <$> studentSelectWidget state
            ]
        ]
  where
    notBoardAcc = workQueueCache.account.type /= BoardAccount

    -- | Next/cancel buttons
    actionBtns :: Widget HTML SelectStudentStepAction
    actionBtns = D.div
        [ P.className "btn-group" ] $ catMaybes
        [ Just $ D.button
            [ P.className "btn cancel"
            , Cancel <$ P.onClick
            ]
            [ D.text $ lookupTranslation "cancel" ]
        , justIf (not $ isWorkQueueEmpty workQueueState) $ D.button
            [ P.className "btn export"
            , Export <$ P.onClick
            ]
            [ D.text $ lookupTranslation "export-pdf" ]
        , Just $ D.button
            [ P.className $ "btn btn-primary"
            , P.disabled $ Sel.isEmpty state.students
            , Done <$ P.onClick
            ]
            [ D.text $ lookupTranslation "next" ]
        ]

setTeacherSelect :: WorkQueueState -> WorkQueueUiState -> Select SearchTeacher -> SelectStudentStepAction
setTeacherSelect workQueueState workQueueUi teacher =
    QueueChanged
        (workQueueState { teacher = _.value <$> selectedItem teacher })
        (workQueueUi { teacher = teacher })

setSemesterSelect :: WorkQueueState -> WorkQueueUiState -> Select SearchSemester -> SelectStudentStepAction
setSemesterSelect workQueueState workQueueUi semester =
    QueueChanged
        (workQueueState { semester = _.value <$> selectedItem semester })
        (workQueueUi { semester = semester })

selectStudentModal :: forall a. Widget HTML a
    -> String -> Maybe SearchState -> Widget HTML (ModalResult SelectStudentState)
selectStudentModal outer msg search = modalWidget' outer $ do
    result <- loadingWidget <|> (affAction <<< runExceptT) do
        sdata <- ExceptT $ runGraphQL searchDataQuery
        let state = initState' search sdata
        page <- ExceptT $ runGraphQL $ searchStudents $ stateToQuery state.search
        pure $ state { page = Just page }
    case result of
        Right state -> runSelectStudentModal msg state
        Left err -> errorModalWidget err

runSelectStudentModal :: String -> SelectStudentState -> Widget HTML (ModalResult SelectStudentState)
runSelectStudentModal msg state = loopState state $ studentSelectModalWidget msg

-- | Render the student select widget and WorkQueue with the given state.
studentSelectModalWidget :: String -> SelectStudentState
    -> Widget HTML (Either SelectStudentState (ModalResult SelectStudentState))
studentSelectModalWidget msg state = D.div
    [ P.className "student-select-wrapper modal-grid" ]
    [ D.h1
        [ P.className "modal-heading" ]
        [ D.text msg ]
    , D.div
        [ P.className "modal-body" ]
        [ Left <$> studentSelectWidget state ]
    , actionBtns
    ]
  where
    actionBtns = D.div
        [ P.className "btn-group modal-buttons" ]
        [ D.button
            [ Right ModalClosed <$ P.onClick
            , P.className "btn"
            ]
            [ D.text $ lookupTranslation "cancel"
            ]
        , D.button
            [ Right (ModalReturned state) <$ P.onClick
            , P.className "btn btn-primary"
            ]
            [ D.text $ lookupTranslation "copy-forms" ]
        ]



-- | Runs the student select widget and returns a new state on every change.
studentSelectWidget :: SelectStudentState -> Widget HTML SelectStudentState
studentSelectWidget state = renderStudentSelect state >>= case _ of
    Left state' -> pure state'
    Right act -> handleAction state act

-- | Handle a `SelectStudentAction` and return the appropriate result after
-- | maybe recursing.
handleAction :: SelectStudentState -> SelectStudentAction -> Widget HTML SelectStudentState
handleAction state' (ChangeSearch sstate) =
    let state = state' { search = sstate }
        handleResult = case _ of
            Right act -> handleAction state act
            Left state'' -> pure state''
    -- When the search changes, start a timer and re-render the widget.
    in ((Nothing <$ affAction (delay debounceDelay)) <|> (Just <$> renderStudentSelect state)) >>=
    case _ of
        -- If the timer expires before another action is returned, load results.
        Nothing -> refreshSearchWidget state >>= handleResult
        -- Otherwise, handle whatever the user did in the UI.
        Just res' -> handleResult res'
handleAction state (ChangePage newpage) = pageChangeWidget state newpage >>= case _ of
    Right act -> handleAction state act
    Left state' -> pure state'
handleAction state (QueryResult students) = pure $ updatePage students $ state { status = SNormal }
handleAction state (QueryFailed err) = do
    _ <- liftEffect $ reportError err
    pure $ state { status = SError err }

-- | Executes and waits on the current search query while displaying the select
-- | widget.
-- |
-- | When a result is returned by either, it will be handled by `handleAction`.
refreshSearchWidget :: SelectStudentState
    -> Widget HTML (Either SelectStudentState SelectStudentAction)
refreshSearchWidget state = renderLoading state $ runSearch state

pageChangeWidget :: SelectStudentState -> Int
    -> Widget HTML (Either SelectStudentState SelectStudentAction)
pageChangeWidget state idx = renderLoading state $ runPageChange state idx

renderLoading :: SelectStudentState -> Aff SelectStudentAction
    -> Widget HTML (Either SelectStudentState SelectStudentAction)
renderLoading state load = do
    res <- (Right <$> renderStudentSelect state { status = SLoading }) <|>
           (Left <$> affAction load)
    case res of
        Right res' -> pure res'
        Left res' -> pure $ Right res'

-- | Runs the current search query and returns a state with the student list
-- | updated.
runSearch :: SelectStudentState -> Aff SelectStudentAction
runSearch state = do
    result <- runGraphQL $ searchStudents $ stateToQuery state.search
    case result of
        Left err -> pure $ QueryFailed err
        Right page -> pure $ QueryResult page

runPageChange :: SelectStudentState -> Int -> Aff SelectStudentAction
runPageChange { page: Just page } newpage = do
    result <- changePage newpage page
    case result of
         Left err -> pure $ QueryFailed err
         Right page' -> pure $ QueryResult page'
runPageChange _ _ = pure $ QueryFailed $ StringError "Attempted to change page when no page was loaded."

renderStudentSelect :: SelectStudentState -> Widget HTML (Either SelectStudentState SelectStudentAction)
renderStudentSelect state = D.section
    [ P.className "select-students" ]
    [ D.div [ P.className "recent-form-group heading" ]
        [ Right <$> ChangeSearch <$> searchWidget state.search
        , case state.status of
            SNormal -> studentList state
            SLoading -> loadingWidget
            SError err -> D.text $ show err
        ]
    ]

-- | List of checkboxes for selecting students from the search results.
studentList :: SelectStudentState -> Widget HTML (Either SelectStudentState SelectStudentAction)
studentList { page: Nothing } = loadingWidget
studentList state@{ page: Just page } | Array.length (pageItems page) > 0 = do
    D.div [ P.className "student-select" ] (checkboxes <> [ pager ])
  where
    pager = Right <$> ChangePage <$> pagerWidget page
    checkboxes = (Left <$> selectAllCheck state) `cons` ((map Left) <$> studentChecks)
    studentChecks =
        map (\st -> studentCheck st state) (pageItems page)
studentList {search} | hasFiltersSet search = D.div
    [ P.className "student-select" ]
    [ D.text $ lookupTranslation "nothing-here-clearable"
    , D.button
        [ P.className "btn btn-primary"
        , Right (ChangeSearch initSearchState) <$ P.onClick
        ]
        [ D.text $ lookupTranslation "clear-filters" ]
    ]
studentList _ = D.div
    [ P.className "student-select" ]
    [ D.text $ lookupTranslation "nothing-here" ]

-- | Checkbox for selecting all items.
selectAllCheck :: SelectStudentState -> Widget HTML SelectStudentState
selectAllCheck state@{ page: Just page } = state { students = _ } <$>
    Sel.selectAllCheckbox' (pageItems page) selectAllCheck_ state.students
selectAllCheck { page: Nothing } = loopInfinitely $ selectAllCheck_ false

selectAllCheck_ :: Boolean -> Widget HTML Boolean
selectAllCheck_ val = D.div
        [ P.className "student-sel-check" ]
        [ labelCheckbox "select-all" (lookupTranslation "select-all") val ]

-- | Checkbox for selecting a student from the search results.
studentCheck :: SelStudent -> SelectStudentState -> Widget HTML SelectStudentState
studentCheck student state = state { students = _ } <$> D.div
    [ P.className "student-sel-check" ]
    [ Sel.selectionCheckbox student.id student (labelCheckbox (innerId student.id) name) state.students
    , renderTags $ tagImported student
    ]
  where
    name = studentName state.account student


renderTags :: forall a. SelStudent -> Widget HTML a
renderTags { tags } = D.div [ P.className "student-tags" ] $ map renderTag tags
  where
    renderTag :: { name :: String, color :: String } -> Widget HTML a
    renderTag { name, color } = D.span
        [ P.className "student-tag"
        , P.style { backgroundColor: color, color: "#000000" }
        ]
        [ D.text name ]